import React, { useState } from 'react';
import { Card, Input, Button, Tag, Spin, Modal, message, Tooltip, Badge } from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { medicineService, Medicine, AuxiliaryData } from '../../services/medicineService';
import MedicineMobileModal from '../../components/medicines/MedicineMobileModal';
import styles from './MedicinesMobilePage.module.css';

const MedicinesMobilePage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState<Medicine | undefined>();
  const [filters, setFilters] = useState({
    category: '',
    dosageForm: '',
    manufacturer: ''
  });

  const queryClient = useQueryClient();

  // Query danh sách sản phẩm
  const { data: medicines = [], isLoading } = useQuery(
    ['medicines', searchText],
    () => searchText ? medicineService.searchMedicines(searchText) : medicineService.getMedicines(),
    {
      staleTime: 5 * 60 * 1000, // 5 phút
    }
  );

  // Query auxiliary data cho filters
  const { data: auxiliaryData, isLoading: isLoadingAuxiliaryData } = useQuery<AuxiliaryData>(
    'auxiliary-data',
    medicineService.getAuxiliaryData,
    {
      staleTime: 10 * 60 * 1000, // 10 phút
      retry: 3,
      onError: (error) => {
        console.error('Lỗi khi tải dữ liệu phụ trợ:', error);
      }
    }
  );

  // Mutation xóa sản phẩm
  const deleteMutation = useMutation(medicineService.deleteMedicine, {
    onSuccess: () => {
      message.success('Xóa sản phẩm thành công!');
      queryClient.invalidateQueries('medicines');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa sản phẩm');
    }});

  const handleAdd = () => {
    setSelectedMedicine(undefined);
    setModalVisible(true);
  };

  const handleEdit = (medicine: Medicine) => {
    setSelectedMedicine(medicine);
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xóa sản phẩm này?',
      content: 'Hành động này không thể hoàn tác.',
      okText: 'Có, Xóa',
      okType: 'danger',
      cancelText: 'Không',
      onOk: () => {
        deleteMutation.mutate(id);
      },
      onCancel: () => {
        // Không làm gì
      }});
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ category: '', dosageForm: '', manufacturer: '' });
    setSearchText('');
  };

  // Lọc dữ liệu theo filters
  const filteredMedicines = medicines.filter((medicine: Medicine) => {
    const matchesCategory = !filters.category || medicine.category === filters.category;
    const matchesDosageForm = !filters.dosageForm || medicine.dosage_form === filters.dosageForm;
    const matchesManufacturer = !filters.manufacturer ||
      medicine.manufacturer.toLowerCase().includes(filters.manufacturer.toLowerCase());
    return matchesCategory && matchesDosageForm && matchesManufacturer;
  });

  return (
    <div className={styles['medicines-mobile-container']}>
      {/* HEADER */}
      <header className={styles['medicines-mobile-header']}>
        {/* Thanh tìm kiếm */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Input
            className={styles['medicines-mobile-search']}
            style={{ width: '100%', padding: '12px 40px 12px 16px', border: '1px solid #ced4da', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
            placeholder="Tìm kiếm sản phẩm..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 18 }}>
            <ReloadOutlined />
          </span>
        </div>
        {/* Nút làm mới */}
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            style={{ flexGrow: 1, padding: 12, fontSize: 16, fontWeight: 600, borderRadius: 8, border: '1px solid #ced4da', color: '#212529', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries('medicines')}
          >
            Làm mới
          </Button>
        </div>
        {/* Bộ lọc */}
        <div style={{ marginTop: 12, marginBottom: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Input
            placeholder="Lọc theo nhà sản xuất"
            allowClear
            value={filters.manufacturer}
            onChange={e => handleFilterChange('manufacturer', e.target.value)}
            className={styles['medicines-mobile-filter-input']}
            style={{ flex: 1, minWidth: 120 }}
          />
          <select
            className={styles['medicines-mobile-select']}
            value={filters.category}
            onChange={e => handleFilterChange('category', e.target.value)}
            disabled={isLoadingAuxiliaryData || !auxiliaryData?.categories?.length}
            style={{ flex: 1, minWidth: 120 }}
          >
            <option value="">Danh mục</option>
            {auxiliaryData?.categories?.map(category => (
              <option key={category.id} value={category.name}>{category.name}</option>
            ))}
          </select>
          <select
            className={styles['medicines-mobile-select']}
            value={filters.dosageForm}
            onChange={e => handleFilterChange('dosageForm', e.target.value)}
            disabled={isLoadingAuxiliaryData || !auxiliaryData?.dosageForms?.length}
            style={{ flex: 1, minWidth: 120 }}
          >
            <option value="">Dạng bào chế</option>
            {auxiliaryData?.dosageForms?.map(form => (
              <option key={form.id} value={form.name}>{form.name}</option>
            ))}
          </select>
          {(filters.category || filters.dosageForm || filters.manufacturer || searchText) && (
            <Button
              type="link"
              icon={<FilterOutlined />}
              onClick={clearFilters}
              size="small"
              style={{ minWidth: 80 }}
            >
              Xóa bộ lọc
            </Button>
          )}
        </div>
      </header>
      <Spin spinning={isLoading}>
        <div className={styles['medicines-mobile-list']}>
          {filteredMedicines.map((item: Medicine) => (
            <div
              key={item.id}
              className={styles['medicines-mobile-card']}
              style={{
                maxWidth: 420,
                width: '100%',
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb',
                margin: '0 auto 4px',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#1890ff', textAlign: 'left' }}>{item.name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, textAlign: 'left' }}>Mã: {item.code}</div>
                    <div style={{ fontSize: 11, color: '#999', textAlign: 'left' }}>SĐK: {item.registration_number}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Tag color={item.is_active ? 'green' : 'red'} style={{ margin: 0 }}>
                      {item.is_active ? 'Hoạt động' : 'Không hoạt động'}
                    </Tag>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 15, textAlign: 'left' }}>
                  <div><strong>Hoạt chất:</strong> {item.active_ingredient}</div>
                  <div><strong>Dạng bào chế:</strong> {item.dosage_form}</div>
                  <div><strong>Hàm lượng:</strong> {item.strength}</div>
                  <div><strong>Nhà sản xuất:</strong> {item.manufacturer}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Badge
                      status={item.min_stock_level > 0 ? 'success' : 'error'}
                      text={`Tối thiểu: ${item.min_stock_level} ${item.unit}`}
                    />
                    <Badge
                      status={item.max_stock_level > 0 ? 'success' : 'error'}
                      text={`Tối đa: ${item.max_stock_level} ${item.unit}`}
                    />
                  </div>
                  <div>
                    <Tag color={auxiliaryData?.categories.find(c => c.name === item.category)?.color || '#1890ff'} style={{ margin: 0 }}>
                      {item.category}
                    </Tag>
                  </div>
                </div>
              </div>
              <div style={{ background: '#f9fafb', padding: '12px 24px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => handleEdit(item)} className={styles['medicines-mobile-edit-btn']} style={{ padding: 8, borderRadius: '50%', color: '#faad14', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <EditOutlined />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className={styles['medicines-mobile-delete-btn']} style={{ padding: 8, borderRadius: '50%', color: 'red', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <DeleteOutlined />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filteredMedicines.length === 0 && (
            <div className={styles['medicines-mobile-empty']}>Không có sản phẩm nào phù hợp.</div>
          )}
        </div>
      </Spin>
      {/* Modal thêm/sửa thuốc */}
      <MedicineMobileModal
        visible={modalVisible}
        medicine={selectedMedicine}
        onCancel={() => {
          setModalVisible(false);
          setSelectedMedicine(undefined);
        }}
        onSuccess={() => {
          setModalVisible(false);
          setSelectedMedicine(undefined);
        }}
      />
      {/* Floating Add Button */}
      <button
        onClick={handleAdd}
        className={styles['medicines-mobile-add-btn']}
        style={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: 100,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#0d6efd',
          color: '#fff',
          border: 'none',
          boxShadow: '0 4px 16px rgba(13,110,253,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
        title="Thêm sản phẩm mới"
      >
        <PlusOutlined />
      </button>
    </div>
  );
};

export default MedicinesMobilePage;
